/**
 * One badge for where an item stands (docs/studio-console-redesign.md, section 3): is it in the
 * app, and did my edits go out? From the backend's publishState, refined by the item's
 * availability window (Scheduled before `from`, Ended after `until`).
 *
 * The colours are the fixed status tones (never the brand's accent), and every badge also has an
 * icon and a word, so nothing depends on colour alone. Pure (no imports at run time).
 */

export type PublishState = "draft" | "new" | "live" | "changed" | "leaving" | "archived" | "unknown";
export type BadgeState = PublishState | "scheduled" | "ended";

/** The four fixed status tones of the kit (the fifth, info, is the brand's accent: not for status). */
export type StatusTone = "neutral" | "positive" | "warning" | "critical";

export interface Badge {
  state: BadgeState;
  label: string;
  /** One plain sentence: what this means for viewers, for a tooltip or a legend. */
  meaning: string;
  tone: StatusTone;
  /** A kit icon name (web-ui/components/Icon). */
  icon: string;
}

const BADGES: Record<BadgeState, Omit<Badge, "state">> = {
  draft: { label: "Draft", meaning: "Not in the app. It stays out until you show it in the app and publish.", tone: "neutral", icon: "edit" },
  new: { label: "Not live yet", meaning: "Goes into the app at your next publish.", tone: "warning", icon: "hourglass" },
  live: { label: "Live", meaning: "In the app exactly as saved.", tone: "positive", icon: "check-circle" },
  changed: { label: "Changes not live", meaning: "In the app, with saved changes that go out at your next publish.", tone: "warning", icon: "reset" },
  leaving: { label: "Leaving the app", meaning: "In the app now; your next publish takes it out.", tone: "critical", icon: "logout" },
  archived: { label: "Archived", meaning: "Put away. Not in the app.", tone: "neutral", icon: "block" },
  scheduled: { label: "Scheduled", meaning: "In the app from its start date.", tone: "neutral", icon: "calendar-1" },
  ended: { label: "Ended", meaning: "Its end date has passed, so the app no longer shows it.", tone: "neutral", icon: "clock" },
  unknown: { label: "Saved", meaning: "Saved. Whether it is live shows after your next publish.", tone: "neutral", icon: "info" },
};

export const BADGE_ORDER: BadgeState[] = ["draft", "new", "live", "changed", "leaving", "archived", "scheduled", "ended", "unknown"];

export interface Availability {
  from?: string;
  until?: string;
}

/**
 * The badge for an item. The window only refines states in which the item is (or is about to be)
 * in the app: a draft is a draft whatever its dates, and leaving wins over ended.
 */
export function badgeFor(state: PublishState | string | undefined, availability?: Availability | null, now: number = Date.now()): Badge {
  const known: PublishState = (["draft", "new", "live", "changed", "leaving", "archived", "unknown"] as const).includes(state as PublishState) ? (state as PublishState) : "unknown";
  let shown: BadgeState = known;
  if (known === "live" || known === "new" || known === "changed" || known === "unknown") {
    const from = availability?.from ? Date.parse(availability.from) : NaN;
    const until = availability?.until ? Date.parse(availability.until) : NaN;
    if (!Number.isNaN(until) && until <= now) shown = "ended";
    else if (!Number.isNaN(from) && from > now && known === "live") shown = "scheduled";
  }
  return { state: shown, ...BADGES[shown] };
}

export function badgeOf(state: BadgeState): Badge {
  return { state, ...BADGES[state] };
}

/** In the app now (whatever happens at the next publish). */
export const isInApp = (state: PublishState | string | undefined) => state === "live" || state === "changed" || state === "leaving";

/** What the next publish changes for this item. */
export const isPending = (state: PublishState | string | undefined) => state === "new" || state === "changed" || state === "leaving";
